import { Component, computed, HostListener, input, output, signal, viewChild, viewChildren } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidAudioButton } from '../../components/rid-audio-button/rid-audio-button';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidCompressableKlankCard } from '../../components/rid-compressable-klank-card/rid-compressable-klank-card';
import { RidCursor } from '../../components/rid-cursor/rid-cursor';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidSegmentedWordCard } from '../../components/rid-segmented-word-card/rid-segmented-word-card';
import { RidTextInput } from '../../components/rid-text-input/rid-text-input';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidVerticalSpacer } from '../../components/rid-vertical-spacer/rid-vertical-spacer';
import { WordToKlankConverter } from '../../utils/klank.utils';
import { calculateLevenshteinDistance } from '../../utils/math.utils';

@Component({
  selector: 'rid-dictation-digital',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidOverlayFeedback,
    RidAudioButton,
    RidBottomButton,
    RidTextInput,
    RidCompressableKlankCard,
    RidVerticalSpacer,
    RidCursor,
    RidSegmentedWordCard,
  ],
  templateUrl: './rid-dictation-digital.html',
  host: {
    class: 'block',
  },
})
export class RidDictationDigital {
  words = input.required<{ word: string; audio: string }[]>();
  errorMargin = input<number>(0.5);
  maxCloseAttempts = input<number>(2);

  index = signal(0);
  word = computed(() => this.words()[this.index()]);
  state = signal<{ type: 'WRITING' } | { type: 'FEEDBACK' } | { type: 'REVEAL' }>({
    type: 'WRITING',
  });
  text = signal<string[]>([]);
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  cards = viewChildren<RidCompressableKlankCard>('cards');
  appearance = signal<'DEFAULT' | 'CORRECT' | 'INCORRECT'>('DEFAULT');
  progress = signal(0);
  instruction = signal('Typ het woord dat je hoort');
  closeAttempts = signal(0);

  split = WordToKlankConverter.breakupWord.bind(WordToKlankConverter);

  finished = output<void>();

  async check() {
    const state = this.state();
    if (state.type !== 'WRITING') return;

    const text = this.text().join('');
    const correctWord = this.word().word;
    this.state.set({ type: 'FEEDBACK' });

    if (text === correctWord) {
      const isLast = this.index() + 1 >= this.words().length;
      if (isLast) this.progress.update((p) => p + 1);
      await this.showCorrect();

      this.next();
    } else {
      const distance = calculateLevenshteinDistance(text.split(''), correctWord.split(''));
      const error = distance / text.length;
      const isCloseAttempt = error < this.errorMargin();
      if (isCloseAttempt) this.closeAttempts.update((c) => c + 1);
      const doReveal = this.closeAttempts() >= this.maxCloseAttempts();

      if (!doReveal) {
        if (isCloseAttempt) this.instruction.set('Bijna! Probeer het nog een keer');
        else this.instruction.set('Probeer het nog eens');
      } else {
        this.instruction.set('Het goede antwoord was...');
      }
      await this.showIncorrect();

      if (doReveal) {
        this.reveal();
      } else {
        this.retry();
      }
    }
  }

  reveal() {
    this.state.set({ type: 'REVEAL' });
  }

  async showCorrect() {
    this.appearance.set('CORRECT');
    RidCompressableKlankCard.compress([...this.cards()]);
    await this.overlayFeedback().showCorrect();
    this.appearance.set('DEFAULT');
  }

  async showIncorrect() {
    this.appearance.set('INCORRECT');
    await this.overlayFeedback().showIncorrect();
    this.appearance.set('DEFAULT');
  }

  retry() {
    this.state.set({ type: 'WRITING' });
    this.text.set([]);
  }

  next() {
    const nextIndex = this.index() + 1;
    if (nextIndex < this.words().length) {
      this.index.update((i) => i + 1);
      this.text.set([]);
      this.state.set({ type: 'WRITING' });
      this.progress.update((p) => p + 1);
      this.closeAttempts.set(0);
      this.instruction.set('Typ het woord dat je hoort');
    } else {
      this.finish();
    }
  }

  finish() {
    this.finished.emit();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.state().type !== 'WRITING') return;

    if (event.key.length === 1 && /^[a-z]$/i.test(event.key)) {
      this.append(event.key.toLowerCase());
      delay(250).then(() => this.compressKlanks(this.split(this.word().word).map((klank) => klank.split(''))));
    }

    if (event.key === 'Backspace') {
      this.pop();
    }
  }

  append(char: string) {
    this.text.set([...this.text(), char]);
  }

  pop() {
    const toDecompress: RidCompressableKlankCard[] = [];
    for (let i = this.cards().length - 1; i >= 0; i--) {
      const card = this.cards()[i];
      if (card.compressed() !== undefined) {
        toDecompress.push(card);
      } else {
        break;
      }
    }
    if (toDecompress.length > 0) {
      RidCompressableKlankCard.decompress(toDecompress);
    }

    this.text.set(this.text().slice(0, -1));
  }

  compressKlanks(pairs: string[][]) {
    const allCards = this.cards();

    for (const sequence of pairs) {
      // Find all occurrences of this sequence in consecutive cards
      for (let i = 0; i <= allCards.length - sequence.length; i++) {
        // Check if the sequence matches starting at position i
        let matches = true;
        for (let j = 0; j < sequence.length; j++) {
          // Get the klank value from each card
          const cardKlank = allCards[i + j].content();
          if (cardKlank !== sequence[j]) {
            matches = false;
            break;
          }
        }

        if (matches) {
          // Compress these consecutive cards that match the sequence
          const cardsToCompress = allCards.slice(i, i + sequence.length);
          RidCompressableKlankCard.compress(cardsToCompress);
        }
      }
    }
  }
}
